<template>
  <vuescroll
    v-if="currentRouteName.length > 0"
    :ops="ops"
    class="menu-wrapper"
  >
    <a-menu
      theme="dark"
      mode="inline"
      :defaultSelectedKeys="[currentRouteName]"
      :defaultOpenKeys="defaultOpenKeys"
      v-model="selectedKeys"
      class="navigation"
    >
      <template v-for="navigationRoute in NAVIGATION_ROUTES">
        <a-sub-menu
          v-if="navigationRoute.nestedRouters"
          :key="navigationRoute.key"
          class="navigation__item"
        >
          <template #title>
            <span
              class="navigation__icon"
              :class="`navigation__icon--${navigationRoute.icon}`"
            />
            <span class="navigation__label">{{ $t(navigationRoute.translationPath) }}</span>
            <div
              class="navigation__counter"
              :class="{
                'navigation__counter--centered': !isSidebarOpened,
                'navigation__counter--unvisible': !navigationCountersMap[navigationRoute.routeName]
              }"
            >
              {{ navigationCountersMap[navigationRoute.routeName] }}
            </div>
          </template>
          <a-menu-item
            v-for="nestedNavigationRoute in navigationRoute.nestedRouters"
            :key="nestedNavigationRoute.key"
            class="navigation__item navigation__item--nested"
            @click="handleRoute(nestedNavigationRoute.routeName)"
          >
            <a-tooltip placement="topLeft">
              <template slot="title">
                <span>prompt text</span>
              </template>
            </a-tooltip>
            <span class="navigation__label">{{ $t(nestedNavigationRoute.translationPath) }}</span>
            <span
              class="navigation__counter navigation__counter--submenu"
              :class="{
                'navigation__counter--unvisible':
                  !navigationCountersMap[nestedNavigationRoute.routeName]
              }"
            >
              {{ navigationCountersMap[nestedNavigationRoute.routeName] }}
            </span>
          </a-menu-item>
        </a-sub-menu>

        <a-menu-item
          v-else
          :key="navigationRoute.key"
          class="navigation__item"
          :class="{ 'ant-menu-item-selected': currentRouteName === navigationRoute.routeName }"
          :title="$t(navigationRoute.translationPath)"
          @click="handleRoute(navigationRoute.routeName)"
        >
          <span
            class="navigation__icon"
            :class="`navigation__icon--${navigationRoute.icon}`"
          />
          <span class="navigation__label">{{ $t(navigationRoute.translationPath) }}</span>
          <span
            class="navigation__counter"
            :class="{
              'navigation__counter--centered': !isSidebarOpened,
              'navigation__counter--unvisible': !navigationCountersMap[navigationRoute.routeName]
            }"
          >
            {{ navigationCountersMap[navigationRoute.routeName] }}
          </span>
        </a-menu-item>
      </template>

      <a-menu-item class="navigation__divider" />

      <a-menu-item
        class="navigation__item"
        :title="$t('navbar.messenger')"
        key="messenger"
        @click="handleRoute('messenger')"
      >
        <span class="navigation__icon navigation__icon--messenger" />
        <span class="navigation__label">{{ $t("navbar.messenger") }}</span>
        <span
          class="navigation__counter"
          :class="{
            'navigation__counter--centered': !isSidebarOpened,
            'navigation__counter--unvisible': !navigationCountersMap['messenger']
          }"
        >
          {{ navigationCountersMap["messenger"] }}
        </span>
      </a-menu-item>
    </a-menu>
  </vuescroll>
</template>

<script>
import vuescroll from "vuescroll"
import { mapGetters, mapMutations, mapState } from "vuex"

import { NAVIGATION_ROUTES } from "@/consts/navigation"
import {
  getNewInvoicesCount,
  getNewOrdersCount,
  getNewReturnsCount,
  getUnreadSupportTicketsCount
} from "@/services/notificationsService"
import { SET_SUPPORT_TICKETS_COUNT } from "@/store/modules/notifications/mutationTypes"

export default {
  name: "DashboardSidebarNavigation",
  components: {
    vuescroll
  },
  props: {
    isSidebarOpened: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      NAVIGATION_ROUTES,
      ops: {
        vuescroll: {},
        scrollPanel: {},
        rail: {
          gutterOfEnds: "10px"
        },
        bar: {
          background: "#c6c6c6",
          opacity: "0.6",
          size: "2px",
          keepShow: true
        }
      },
      ordersCount: 0,
      returnsCount: 0,
      invoicesCount: 0,
      updateCountsTimerId: null,
      selectedKeys: []
    }
  },
  computed: {
    ...mapState("messenger", {
      unreadMessages: (state) => state.unreadMessages || 0
    }),
    ...mapState("notifications", {
      newSupportTicketsCount: (state) => state.newSupportTicketsCount
    }),
    ...mapGetters("auth", ["isAuthenticated"]),
    currentRouteGroupKey() {
      return this.NAVIGATION_ROUTES.filter((item) => !!item.nestedRouters).find((item) => {
        return item.nestedRouters.findIndex((nested) => nested.key === this.$route.name) !== -1
      })?.key
    },
    currentRouteName() {
      return this.$route.name || ""
    },
    navigationCountersMap() {
      return {
        messenger: this.unreadMessages,
        orders: this.ordersCount,
        returns: this.returnsCount,
        support: this.newSupportTicketsCount,
        tariffs: this.invoicesCount,
        tariffInvoices: this.invoicesCount
      }
    },
    defaultOpenKeys() {
      return this.isSidebarOpened && this.currentRouteGroupKey ? [this.currentRouteGroupKey] : []
    }
  },
  created() {
    this.updateCounts()
  },
  beforeDestroy() {
    window.clearTimeout(this.updateCountsTimerId)
  },
  watch: {
    // SNT-5798. Змінюємо вибраний ключ в меню для того щоб прибрати ефект вибраного елементу меню після зміни роута не через клік по елементу меню
    $route() {
      this.selectedKeys = [this.currentRouteName]
    }
  },
  methods: {
    ...mapMutations("notifications", [SET_SUPPORT_TICKETS_COUNT]),
    handleRoute(routeName) {
      if (this.currentRouteName === routeName) return

      this.$router.push({ name: routeName })
    },
    updateCounts() {
      window.clearTimeout(this.updateCountsTimerId)

      if (this.isAuthenticated) {
        Promise.all([
          getNewOrdersCount(),
          getNewReturnsCount(),
          getUnreadSupportTicketsCount(),
          getNewInvoicesCount()
        ]).then((values) => {
          const [ordersResponse, returnsResponse, supportResponse, invoicesResponse] = values

          this.ordersCount = ordersResponse.data.count
          this.returnsCount = returnsResponse.data.count
          this.invoicesCount = invoicesResponse.data.count

          this[SET_SUPPORT_TICKETS_COUNT](supportResponse.data.count)

          this.updateCountsTimerId = window.setTimeout(() => {
            this.updateCounts()
          }, 5000)
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$navigation-item-height: 40px;
$navigation-item-gutter: 8px;
$counter-size: 20px;

.menu-wrapper {
  height: calc(100% - 150px);
  overflow: auto;
}

.ant-menu-inline-collapsed {
  .navigation__label {
    display: none;
  }
}

.navigation {
  $root: &;

  padding-top: 8px;
  display: flex;
  flex-direction: column;
  user-select: none;

  &__item {
    margin-top: 0;
    overflow: visible !important;

    &--nested {
      #{$root}__label {
        margin-left: 9px;
      }
    }
  }

  &__label {
    color: $font-color-white-accent;
    margin-left: 16px;
  }

  &__counter {
    min-width: $counter-size;
    line-height: $counter-size;
    border-radius: calc(#{$counter-size} / 2);
    font-size: $base-font-size;
    background-color: $green-color-6;
    color: #fff;
    pointer-events: none;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 0 4px;
    position: absolute;
    top: 0;
    left: 16px;
    transform: translateY(-4px);
    opacity: 1;

    @include transition(opacity, $transition-duration-fast);

    &--centered {
      left: 50%;
      transform: translate(-50%, -4px);
    }

    &--submenu {
      position: relative;
      transform: unset;
      left: 4px;
    }

    &--unvisible {
      display: none;
    }
  }

  &__divider {
    height: 1px !important;
    border-bottom: 1px solid $sider-border-color;
    margin-top: $navigation-item-gutter;
  }

  &__icon {
    display: inline-flex;
    flex-basis: $sider-icon-size-opened;
    min-width: $sider-icon-size-opened;
    min-height: $sider-icon-size-opened;
    width: $sider-icon-size-opened;
    height: $sider-icon-size-opened;
    font-size: $sider-icon-size-opened;
    position: relative;
    top: 2px;

    &--dashboard {
      background: url("../../assets/icons/sidebar-dashboard.svg") no-repeat;
    }

    &--offers {
      background: url("../../assets/icons/sidebar-offers.svg") no-repeat;
    }

    &--orders {
      background: url("../../assets/icons/sidebar-orders.svg") no-repeat;
    }

    &--reviews {
      background: url("../../assets/icons/sidebar-reviews.svg") no-repeat center;
    }

    &--delivery {
      background: url("../../assets/icons/sidebar-delivery.svg") no-repeat;
    }

    &--messenger {
      background: url("../../assets/icons/sidebar-messenger.svg") no-repeat;
    }

    &--synchronization {
      background: url("../../assets/icons/sidebar-synchronization.svg") no-repeat;
    }

    &--subscribers {
      background: url("../../assets/icons/sidebar-subscribers.svg") no-repeat;
    }

    &--support {
      background: url("../../assets/icons/sidebar-support.svg") no-repeat;
    }

    &--returns {
      background: url("../../assets/icons/sidebar-returns.svg") no-repeat;
    }

    &--adversting {
      background: url("../../assets/icons/sidebar-adversting.svg") no-repeat;
    }

    &--tarrifs {
      background: url("../../assets/icons/sidebar-tarrifs.svg") no-repeat;
    }

    &--keys {
      background: url("../../assets/icons/sidebar-key.svg") no-repeat;
    }
  }
}
</style>

<style>
.ant-menu-submenu-title {
  overflow: visible !important;
}
</style>
